import React, { useEffect, useRef, useState } from 'react';
import { fetchClaims, Claim } from '../../api/claims';
import './lanes.css';

/**
 * Shared claim-search widget (Tasks Rebuild Task 12) -- the debounced
 * "search by client name/email/agreement number, click a result" idiom that
 * existed in three separate places before this task: CreateTaskModal.tsx
 * (Task 8), ClientLane.tsx's per-sender "assign all" modal (Task 9), and the
 * legacy Tasks.tsx's assign/reassign dialog. Extracted once here per the
 * Task 12 brief ("shared claim-picker component ... dedupe"); CreateTaskModal
 * has been refactored to use it (see its diff). ClientLane's own assign-all
 * modal is deliberately left as its own already-tested inline implementation
 * for this task (lower regression risk) but could adopt this component in a
 * later cleanup pass -- see task-12-report.md.
 *
 * Same DOM/class names as the original inline markup (`lane-modal__*`) so
 * existing CSS (and any test that queries by those classes/placeholder text)
 * keeps working unchanged.
 */

export interface ClaimPickerProps {
  /** Currently-selected claim, if any -- controlled by the parent so it can
   * be reset alongside the rest of a form/modal's state. */
  selectedClaim: Claim | null;
  onSelect: (claim: Claim) => void;
  label?: string;
  placeholder?: string;
  autoFocus?: boolean;
  /** Customises the secondary line under a result's name -- CreateTaskModal
   * shows the lender there, ClientLane doesn't. Defaults to "email • ID". */
  formatMeta?: (claim: Claim) => React.ReactNode;
  /** After a result is clicked, collapse the results list and put a
   * human-readable label in the search box instead of leaving the raw typed
   * query -- matches CreateTaskModal's original UX. Default true. */
  collapseOnSelect?: boolean;
  /** Search term to start with -- e.g. the email's own claim reference, so
   * staff don't paste one from the clipboard (wrong-claim attach guard). */
  initialTerm?: string;
  /** Quick-pick chips (the email's references) that fill the search box.
   * Used instead of `initialTerm` when the email quotes several refs. */
  suggestions?: string[];
}

const defaultFormatMeta = (c: Claim): React.ReactNode => (
  <>{c.email} &bull; ID: {c.id}</>
);

export const ClaimPicker: React.FC<ClaimPickerProps> = ({
  selectedClaim,
  onSelect,
  label = 'Search claims:',
  placeholder = 'Search by client name, email, or agreement number...',
  autoFocus,
  formatMeta = defaultFormatMeta,
  collapseOnSelect = true,
  initialTerm = '',
  suggestions,
}) => {
  const [term, setTerm] = useState(initialTerm);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Claim[]>([]);
  // Latest-wins guard (same idea as the lane bodies' requestIdRef): every
  // term change bumps the id, so a slow response for an older term can
  // never overwrite the results for the one now in the box.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    if (!term || term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetchClaims(1, 20, term);
        if (requestId !== requestIdRef.current) return; // stale response, dropped
        setResults(res.items || []);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
      } finally {
        if (requestId === requestIdRef.current) setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [term]);

  // Unmount: invalidate anything still in flight.
  useEffect(() => () => { requestIdRef.current += 1; }, []);

  const handleSelect = (claim: Claim) => {
    onSelect(claim);
    if (collapseOnSelect) {
      setResults([]);
      setTerm(`${claim.firstName || ''} ${claim.lastName || ''} - ${claim.email || ''}`.trim());
    }
  };

  return (
    <div className="lane-modal__field">
      {label && <label>{label}</label>}
      <input
        type="text"
        className="lane-modal__input"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {suggestions && suggestions.length > 0 && (
        <div className="lane-modal__ref-chips">
          <span className="lane-modal__hint">References in this email:</span>
          {suggestions.map((ref) => (
            <button
              key={ref}
              type="button"
              className={`lane-modal__ref-chip ${term === ref ? 'lane-modal__ref-chip--active' : ''}`.trim()}
              onClick={() => setTerm(ref)}
            >
              {ref}
            </button>
          ))}
        </div>
      )}
      {searching && <div className="lane-modal__hint">Searching...</div>}
      {!searching && results.length > 0 && (
        <div className="lane-modal__claim-results">
          {results.map((c) => (
            <div
              key={c.id}
              onClick={() => handleSelect(c)}
              className={`lane-modal__claim-result ${selectedClaim?.id === c.id ? 'lane-modal__claim-result--selected' : ''}`}
            >
              <div className="lane-modal__claim-result-name">{c.firstName} {c.lastName}</div>
              <div className="lane-modal__claim-result-meta">{formatMeta(c)}</div>
            </div>
          ))}
        </div>
      )}
      {selectedClaim && (
        <div className="lane-modal__hint">Selected Claim ID: {selectedClaim.id}</div>
      )}
    </div>
  );
};

export default ClaimPicker;
